import { useCallback, useRef, MouseEvent, TouchEvent } from 'react'

interface LongPressOptions {
  onLongPress: () => void
  onClick?: () => void
  delay?: number
  enabled?: boolean
}

interface LongPressHandlers {
  onMouseDown: (e: MouseEvent) => void
  onMouseUp: (e: MouseEvent) => void
  onMouseLeave: (e: MouseEvent) => void
  onTouchStart: (e: TouchEvent) => void
  onTouchEnd: (e: TouchEvent) => void
  onTouchMove: (e: TouchEvent) => void
}

/**
 * Hook to detect long press gestures (similar to iOS long-press to reorder apps)
 * Supports both mouse and touch events for web and mobile
 *
 * @param options - Configuration for long press behavior
 * @param options.onLongPress - Callback when long press is detected
 * @param options.onClick - Optional callback for regular click (when press is shorter than delay)
 * @param options.delay - Duration in ms to trigger long press (default: 500ms)
 * @param options.enabled - Whether the hook is active (default: true)
 * @returns Event handlers to spread on the target element
 *
 * @example
 * const longPressHandlers = useLongPress({
 *   onLongPress: () => console.log('Long pressed!'),
 *   onClick: () => console.log('Clicked'),
 * })
 * return <div {...longPressHandlers}>Press me</div>
 */
export function useLongPress({
  onLongPress,
  onClick,
  delay = 500,
  enabled = true,
}: LongPressOptions): LongPressHandlers {
  const timerRef = useRef<number | null>(null)
  const isLongPressRef = useRef(false)

  const start = useCallback(() => {
    if (!enabled) return

    isLongPressRef.current = false
    timerRef.current = window.setTimeout(() => {
      isLongPressRef.current = true
      onLongPress()
    }, delay)
  }, [enabled, onLongPress, delay])

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const handleEnd = useCallback(() => {
    clear()
    if (!isLongPressRef.current && onClick) {
      onClick()
    }
    isLongPressRef.current = false
  }, [clear, onClick])

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button === 0) {
        e.preventDefault()
        start()
      }
    },
    [start]
  )

  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      handleEnd()
    },
    [handleEnd]
  )

  const handleMouseLeave = useCallback(() => {
    clear()
    isLongPressRef.current = false
  }, [clear])

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      start()
    },
    [start]
  )

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      handleEnd()
    },
    [handleEnd]
  )

  const handleTouchMove = useCallback(() => {
    // Cancel the long press if the user moves their finger
    clear()
    isLongPressRef.current = false
  }, [clear])

  return {
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onMouseLeave: handleMouseLeave,
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd,
    onTouchMove: handleTouchMove,
  }
}
